import Component from "@glimmer/component";
import { tracked } from "@glimmer/tracking";
import { hash } from "@ember/helper";
import { action } from "@ember/object";
import { service } from "@ember/service";
import PreferenceCheckbox from "discourse/components/preference-checkbox";
import ComboBox from "discourse/select-kit/components/combo-box";
import DButton from "discourse/ui-kit/d-button";
import { i18n } from "discourse-i18n";
import {
  HEADER_INDICATOR_PREFERENCE_ALL_NEW,
  HEADER_INDICATOR_PREFERENCE_DM_AND_MENTIONS,
  HEADER_INDICATOR_PREFERENCE_NEVER,
  HEADER_INDICATOR_PREFERENCE_ONLY_MENTIONS,
} from "discourse/plugins/chat/discourse/lib/chat-constants";
import {
  chatSoundOptions,
  normalizeChatSoundName,
} from "discourse/plugins/chat/discourse/services/chat-audio-manager";

export default class ChatNotifications extends Component {
  static shouldRender(args, { siteSettings }) {
    return siteSettings.chat_enabled && args.model?.user_option?.chat_enabled;
  }

  @service chatAudioManager;

  @tracked chatSound;
  @tracked headerIndicatorPreference;

  constructor() {
    super(...arguments);
    this.chatSound = normalizeChatSoundName(this.userOption.chat_sound);
    this.headerIndicatorPreference =
      this.userOption.chat_header_indicator_preference;
  }

  get model() {
    return this.args.outletArgs.model;
  }

  get userOption() {
    return this.model.user_option;
  }

  get chatSounds() {
    return chatSoundOptions();
  }

  get headerIndicatorOptions() {
    return [
      {
        name: i18n("chat.header_indicator_preference.all_new"),
        value: HEADER_INDICATOR_PREFERENCE_ALL_NEW,
      },
      {
        name: i18n("chat.header_indicator_preference.dm_and_mentions"),
        value: HEADER_INDICATOR_PREFERENCE_DM_AND_MENTIONS,
      },
      {
        name: i18n("chat.header_indicator_preference.only_mentions"),
        value: HEADER_INDICATOR_PREFERENCE_ONLY_MENTIONS,
      },
      {
        name: i18n("chat.header_indicator_preference.never"),
        value: HEADER_INDICATOR_PREFERENCE_NEVER,
      },
    ];
  }

  @action
  setHeaderIndicatorPreference(value) {
    this.headerIndicatorPreference = value;
    this.model.set("user_option.chat_header_indicator_preference", value);
  }

  @action
  setChatSound(sound) {
    if (sound) {
      this.chatAudioManager?.play(sound, { throttle: false });
    }

    this.chatSound = sound ?? null;
    this.model.set("user_option.chat_sound", sound ?? null);
  }

  @action
  previewChatSound() {
    this.chatAudioManager?.play(this.chatSound, { throttle: false });
  }

  <template>
    <div class="control-group chat-notifications">
      <label class="control-label">{{i18n
          "chat.chat_notifications_title"
        }}</label>

      <PreferenceCheckbox
        class="pref-chat-ignore-channel-wide-mention"
        data-setting-name="chat-ignore-channel-wide-mention"
        @checked={{this.userOption.ignore_channel_wide_mention}}
        @labelKey="chat.ignore_channel_wide_mention.title"
      />

      <div class="controls controls-dropdown">
        <label>{{i18n "chat.sound.title"}}</label>
        <div class="chat-sound-controls">
          <ComboBox
            class="chat-sound"
            @content={{this.chatSounds}}
            @onChange={{this.setChatSound}}
            @options={{hash none="chat.sounds.none"}}
            @value={{this.chatSound}}
            @valueProperty="value"
          />
          {{#if this.chatSound}}
            <DButton
              class="btn-flat chat-sound-preview"
              @action={{this.previewChatSound}}
              @icon="play"
              @title="chat.sound.preview"
            />
          {{/if}}
        </div>
      </div>

      <div class="controls controls-dropdown">
        <label>{{i18n "chat.header_indicator_preference.title"}}</label>
        <ComboBox
          class="chat-header-indicator-preference"
          @content={{this.headerIndicatorOptions}}
          @onChange={{this.setHeaderIndicatorPreference}}
          @value={{this.headerIndicatorPreference}}
          @valueProperty="value"
        />
      </div>
    </div>
  </template>
}
